import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  Apple,
  ChefHat,
  ShoppingCart,
  Calendar,
  Sparkles,
  AlertCircle,
  CheckCircle,
  Loader2,
  RefreshCw,
  Target,
  Settings2
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import AIConsentModal, { hasAIConsent } from '../components/AIConsentModal';
import { MealPlanProgressModal } from '../components/MealPlanProgressModal';
import { FoodProgressModal } from '../components/FoodProgressModal';
import InsufficientCreditsModal from '../components/InsufficientCreditsModal';
import ShoppingListModal from '../components/ShoppingListModal';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface FoodLists {
  allowed: string[];
  forbidden: string[];
  recommendations: string;
}

interface MealPlan {
  id: string;
  days: number;
  createdAt: string;
  data: any;
}

export default function NutritionPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState<'foods' | 'plans'>('foods');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [showFoodProgress, setShowFoodProgress] = useState(false);
  const [showAIConsent, setShowAIConsent] = useState(!hasAIConsent());
  
  // Foods data
  const [foods, setFoods] = useState<FoodLists | null>(null);
  const [foodsError, setFoodsError] = useState('');
  
  // Meal plans data
  const [mealPlans, setMealPlans] = useState<MealPlan[]>([]);

  // Insufficient credits modal
  const [creditsModal, setCreditsModal] = useState<{ open: boolean; available: number; required: number }>({
    open: false, available: 0, required: 0
  });

  // Shopping list modal
  const [shoppingListPlan, setShoppingListPlan] = useState<MealPlan | null>(null);

  const showCreditsModal = (available: number, required: number) => {
    setCreditsModal({ open: true, available, required });
  };

  // Personalized content based on healthGoal
  const getPersonalizedNutritionContent = () => {
    const healthGoal = user?.healthGoal;

    if (!healthGoal) {
      return {
        title: 'Nutriție Personalizată',
        description: 'Setează un obiectiv de sănătate pentru recomandări nutriționale specifice',
        tips: ['Completează profilul', 'Alege un obiectiv clar', 'Primește plan personalizat']
      };
    }

    const nutritionGoals: Record<string, any> = {
      // HEALTHY MODE GOALS
      WEIGHT_LOSS: {
        title: 'Nutriție pentru Slăbit',
        description: 'Plan hipocaloric (deficit 300-500 kcal), rețete light, controlul porțiilor',
        tips: ['Deficit caloric controlat', 'Alimente săturante cu calorii puține', 'Tracking zilnic macros']
      },
      MUSCLE_GAIN: {
        title: 'Nutriție pentru Masă Musculară',
        description: 'Plan hiperproteic (1.6-2g/kg), surplus caloric, timing nutrienți',
        tips: ['Surplus +300-500 kcal/zi', 'Proteine la fiecare masă', 'Post-workout nutrition']
      },
      HEALTHY_LIFESTYLE: {
        title: 'Nutriție Echilibrată',
        description: 'Plan echilibrat: 50% carbo, 30% proteine, 20% grăsimi sănătoase',
        tips: ['Varietate alimente', 'Porții moderate', 'Consistență zilnică']
      },
      REDUCE_BELLY_FAT: {
        title: 'Nutriție Anti-Grăsime Abdominală',
        description: 'Low-carb, focus pe proteine & fibre, reducere zahăr & carbohidrați simpli',
        tips: ['Evită zahărul & carbohidrații simpli', 'Proteine & fibre la fiecare masă', 'Post intermitent (opțional)']
      },
      INCREASE_ENERGY: {
        title: 'Nutriție pentru Energie',
        description: 'Dietă antiinflamatoare, carbo complecși, evitare "food crash"',
        tips: ['Alimente cu index glicemic scăzut', 'Omega-3 & antioxidanți', 'Mese la 3-4 ore']
      },
      CLEAN_EATING: {
        title: 'Alimentație Curată',
        description: 'Fără alimente procesate, max 5 ingrediente, gătit acasă',
        tips: ['Alimente întregi, neprocesate', 'Citește etichetele', 'Gătește acasă']
      },
      REDUCE_SUGAR_CARBS: {
        title: 'Nutriție Low-Carb',
        description: 'Sub 100g carbohidrați/zi, focus pe legume verzi, fără zahăr',
        tips: ['<100g carbo/zi', 'Înlocuiește carbohidrații', 'Atenție la zahăr ascuns']
      },
      BETTER_SLEEP: {
        title: 'Nutriție pentru Somn',
        description: 'Alimente care promovează somnul, evitare cafea după 14:00',
        tips: ['Magneziu & triptofan', 'Evită cafeina după-amiază', 'Cină ușoară']
      },
      REDUCE_BLOATING: {
        title: 'Nutriție Anti-Balonare',
        description: 'Dietă FODMAP, elimină alimente care fermentează',
        tips: ['Protocol FODMAP', 'Jurnal alimentație + reacții', 'Probiotice naturale']
      },
      START_EXERCISE: {
        title: 'Nutriție pentru Fitness',
        description: 'Energie pentru antrenament, recuperare musculară',
        tips: ['Carbo înainte de workout', 'Proteine după', 'Hidratare constantă']
      },
      IMPROVE_FOCUS: {
        title: 'Nutriție pentru Creier',
        description: 'Omega-3, magneziu, antioxidanți pentru funcție cognitivă',
        tips: ['Pește gras (omega-3)', 'Nuci & semințe', 'Afine & verdețuri']
      },
      MAINTAIN_WEIGHT: {
        title: 'Nutriție pentru Menținere',
        description: 'Echilibru caloric perfect, consistență alimentară',
        tips: ['Consumă = Arzi', 'Tracking consistent', 'Flexibilitate 80/20']
      },

      // CHRONIC MODE GOALS
      EASY_STANDING: {
        title: 'Nutriție pentru Forță',
        description: 'Proteine pentru mușchi, calciu pentru oase, nutrienți esențiali',
        tips: ['Proteine calitate', 'Calciu & vitamina D', 'Nutrienți pentru recuperare']
      },
      REDUCE_INFLAMMATION: {
        title: 'Nutriție Antiinflamatoare',
        description: 'Omega-3, curcuma, ghimbir, elimină pro-inflamatori',
        tips: ['Omega-3 din pește', 'Curcuma & ghimbir', 'Elimină zahăr & procesate']
      },
      RECOVER_MUSCLE_STRENGTH: {
        title: 'Nutriție pentru Recuperare',
        description: 'Proteine pentru reconstrucție musculară, aminoacizi esențiali',
        tips: ['Proteine 1.2-1.5g/kg', 'Leucină & BCAA', 'Antioxidanți pentru recuperare']
      },
      CLIMB_STAIRS: {
        title: 'Nutriție pentru Rezistență',
        description: 'Energie susținută, funcție cardiovasculară',
        tips: ['Carbohidrați complecși', 'Fier pentru oxigen', 'Magneziu pentru mușchi']
      },
      REDUCE_JOINT_PAIN: {
        title: 'Nutriție pentru Articulații',
        description: 'Antiinflamatori naturali, colagen, omega-3',
        tips: ['Pește gras 3x/săptămână', 'Colagen & glucozamină', 'Antioxidanți']
      },
      BREATHE_BETTER: {
        title: 'Nutriție pentru Funcție Pulmonară',
        description: 'Anti-mucus, antiinflamator, hidratare',
        tips: ['Evită lactate & zahăr', 'Antioxidanți din fructe', 'Hidratare excelentă']
      },
      STABILIZE_GLUCOSE: {
        title: 'Nutriție pentru Diabet',
        description: 'Low GI, fibră, control glicemie, porții controlate',
        tips: ['Index glicemic scăzut', 'Fibre la fiecare masă', 'Porții mici, frecvente']
      },
      IMPROVE_BALANCE: {
        title: 'Nutriție pentru Echilibru',
        description: 'Nutrienți pentru sistem nervos, vitamina B12',
        tips: ['B12 & magneziu', 'Antioxidanți', 'Hidratare optimă']
      },
      REDUCE_CHRONIC_FATIGUE: {
        title: 'Nutriție Anti-Oboseală',
        description: 'Fier, B12, magneziu, alimente energizante',
        tips: ['Fier & B12 (sânge, ficat)', 'Magneziu pentru energie', 'Evită zahărul']
      },
      IMPROVE_DIGESTION: {
        title: 'Nutriție pentru Digestie',
        description: 'Fără iritanți, dietă FODMAP, probiotice',
        tips: ['Elimină iritanți', 'FODMAP protocol', 'Probiotice naturale']
      },
      REGAIN_MOBILITY: {
        title: 'Nutriție pentru Mobilitate',
        description: 'Antiinflamator, colagen, nutrienți pentru țesuturi',
        tips: ['Proteine pentru țesuturi', 'Vitamina C & colagen', 'Omega-3']
      },
      PREVENT_DISEASE_WORSENING: {
        title: 'Nutriție Protectoare',
        description: 'Plan specific bolii, nutrienți terapeutici',
        tips: ['Adaptat la boală', 'Nutrienți esențiali', 'Evită trigger foods']
      },
      HEALTHY_WEIGHT_LOSS_CHRONIC: {
        title: 'Slăbire Sănătoasă (Adaptată)',
        description: 'Deficit mic (0.25-0.5kg/săptămână), nutrienți esențiali',
        tips: ['Ritm lent & sănătos', 'Toate nutrientele esențiale', 'Adaptat la boală']
      },
      CONTROL_CHRONIC_PAIN: {
        title: 'Nutriție Anti-Durere',
        description: 'Antiinflamatori naturali, magneziu, omega-3',
        tips: ['Dietă antiinflamatoare', 'Magneziu pentru mușchi', 'Evită trigger foods']
      },
      IMPROVE_LIFE_WITH_DISEASE: {
        title: 'Nutriție Personalizată (Boală)',
        description: 'Plan complet adaptat diagnosticului specific',
        tips: ['100% personalizat', 'Bazat pe diagnostic', 'Monitoring continuu']
      }
    };

    return nutritionGoals[healthGoal] || nutritionGoals.HEALTHY_LIFESTYLE;
  };

  const personalizedContent = getPersonalizedNutritionContent();

  useEffect(() => {
    checkExistingFoods();
    loadMealPlans();
  }, []);

  const checkExistingFoods = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      
      // TIMEOUT RAPID: max 2 secunde pentru empty state
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000);
      
      const response = await axios.get(`${API_URL}/api/nutrition/foods`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
        timeout: 2000
      });
      
      clearTimeout(timeoutId);
      
      const hasFoods =
        (response.data.allowed && response.data.allowed.length > 0) ||
        (response.data.forbidden && response.data.forbidden.length > 0) ||
        (response.data.recommendations && response.data.recommendations.length > 0);

      if (hasFoods) {
        setFoods(response.data);
      }
      setFoodsError('');
    } catch (error: any) {
      // Silent error pentru timeout - afișăm empty state
      if (error.code === 'ECONNABORTED' || error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        console.log('⚡ Timeout după 2 sec - afișare instant empty state');
      } else {
        console.error('Error checking foods:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const generateFoods = async () => {
    setGenerating(true);
    setShowFoodProgress(true);
    
    try {
      const token = localStorage.getItem('accessToken');
      
      // Trigger generation (background job)
      axios.post(
        `${API_URL}/api/nutrition/foods/generate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      ).then(() => {
        console.log('Food generation started');
      }).catch(error => {
        console.error('Error starting generation:', error);
        setShowFoodProgress(false);
        setGenerating(false);
        
        if (error.response?.status === 402 || error.response?.data?.code === 'INSUFFICIENT_CREDITS') {
          const available = error.response?.data?.available ?? 0;
          const required = error.response?.data?.required ?? 0;
          showCreditsModal(available, required);
        } else {
          const errorMsg = error.response?.data?.error || 'Eroare la generarea alimentelor';
          setFoodsError(errorMsg);
        }
      });
    } catch (error: any) {
      console.error('Error in generateFoods:', error);
      setShowFoodProgress(false);
      setGenerating(false);
      
      if (error.response?.status === 402 || error.response?.data?.code === 'INSUFFICIENT_CREDITS') {
        const available = error.response?.data?.available ?? 0;
        const required = error.response?.data?.required ?? 0;
        showCreditsModal(available, required);
      } else {
        setFoodsError('Eroare la generarea alimentelor. Verifică dacă ai profilul completat.');
      }
    }
  };
  
  const handleFoodProgressComplete = async () => {
    setShowFoodProgress(false);
    setGenerating(false);
    await checkExistingFoods();
  };
  
  const handleFoodProgressError = (error: string) => {
    setShowFoodProgress(false);
    setGenerating(false);
    // Check if it's a credits error
    if (error.toLowerCase().includes('credite') || error.toLowerCase().includes('credit')) {
      const match = error.match(/(\d+)\s*credite disponibile.*?(\d+)/);
      if (match) {
        showCreditsModal(parseInt(match[1]), parseInt(match[2]));
        return;
      }
    }
    setFoodsError(error);
  };

  const refreshFoods = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${API_URL}/api/nutrition/foods/refresh`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFoods(response.data);
      setFoodsError('');
    } catch (error: any) {
      console.error('Error refreshing foods:', error);
      if (error.response?.status === 402 || error.response?.data?.code === 'INSUFFICIENT_CREDITS') {
        const available = error.response?.data?.available ?? 0;
        const required = error.response?.data?.required ?? 0;
        showCreditsModal(available, required);
      } else {
        const errorMsg = error.response?.data?.error || 'Eroare necunoscută';
        setFoodsError(errorMsg);
      }
    } finally {
      setRefreshing(false);
    }
  };

  const loadMealPlans = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_URL}/api/nutrition/meal-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMealPlans(response.data);
    } catch (error) {
      console.error('Error loading meal plans:', error);
    }
  };

  const generateMealPlan = async (days: 7 | 30) => {
    setGenerating(true);
    setShowProgress(true);
    
    try {
      // Start generation în background
      const token = localStorage.getItem('accessToken');
      
      // Trigger generation (it runs in background)
      axios.post(
        `${API_URL}/api/nutrition/meal-plan/generate`,
        { days },
        { headers: { Authorization: `Bearer ${token}` } }
      ).then(() => {
        // Success handled by progress modal
        console.log('Meal plan generation started successfully');
      }).catch(error => {
        console.error('Error starting meal plan generation:', error);
        setShowProgress(false);
        setGenerating(false);
        
        if (error.response?.status === 402 || error.response?.data?.code === 'INSUFFICIENT_CREDITS') {
          const available = error.response?.data?.available ?? 0;
          const required = error.response?.data?.required ?? 0;
          showCreditsModal(available, required);
        } else {
          const errorMsg = error.response?.data?.error || 'Eroare la generarea planului de mese. Încearcă din nou.';
          alert(`❌ ${errorMsg}`);
        }
      });
    } catch (error: any) {
      console.error('Error in generateMealPlan:', error);
      setShowProgress(false);
      setGenerating(false);
      
      if (error.response?.status === 402 || error.response?.data?.code === 'INSUFFICIENT_CREDITS') {
        const available = error.response?.data?.available ?? 0;
        const required = error.response?.data?.required ?? 0;
        showCreditsModal(available, required);
      } else {
        alert('Eroare la generarea planului de mese. Verifică dacă ai profilul completat.');
      }
    }
  };

  const handleProgressComplete = async () => {
    setShowProgress(false);
    setGenerating(false);
    await loadMealPlans();
  };

  const handleProgressError = (error: string) => {
    setShowProgress(false);
    setGenerating(false);
    // Check if it's a credits error
    if (error.toLowerCase().includes('credite') || error.toLowerCase().includes('credit')) {
      const match = error.match(/(\d+)\s*credite disponibile.*?(\d+)/);
      if (match) {
        showCreditsModal(parseInt(match[1]), parseInt(match[2]));
        return;
      }
    }
    alert(error);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-green-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
      <PageHeader
        title="Nutriție Adaptată"
        subtitle="Alimente și planuri personalizate pentru starea ta"
        showBack={true}
        backTo="/dashboard"
        icon={<Apple className="w-5 h-5" />}
      />

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-xl p-2 mb-6">
          <div className="flex gap-2">
            <button
              onClick={() => setTab('foods')}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                tab === 'foods'
                  ? 'bg-green-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Apple className="w-5 h-5" />
              <span className="hidden sm:inline">Alimente Permise/Interzise</span>
              <span className="sm:hidden">Alimente</span>
            </button>
            <button
              onClick={() => setTab('plans')}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                tab === 'plans'
                  ? 'bg-green-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Calendar className="w-5 h-5" />
              <span className="hidden sm:inline">Planuri de Mese</span>
              <span className="sm:hidden">Planuri</span>
            </button>
          </div>
        </div>

        {/* Personalized Goal Banner */}
        {user?.healthGoal && (
          <div className="mb-6 bg-gradient-to-r from-green-600 via-blue-600 to-purple-600 rounded-2xl p-6 text-white shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <Target className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold">{personalizedContent.title}</h2>
                <p className="text-sm text-green-100">{personalizedContent.description}</p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-3 gap-3">
              {personalizedContent.tips.map((tip: string, idx: number) => (
                <div key={idx} className="flex items-start gap-2 bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                  <div className="w-1.5 h-1.5 bg-white rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-sm">{tip}</span>
                </div>
              ))}
            </div>

            {!user.healthGoal && (
              <button
                onClick={() => navigate('/health-goal')}
                className="mt-4 px-4 py-2 bg-white text-green-600 rounded-lg font-medium hover:bg-green-50 transition-colors"
              >
                Setează Obiectiv
              </button>
            )}
          </div>
        )}

        {/* Foods Tab */}
        {tab === 'foods' && (
          <div className="space-y-6">
            {!foods && !foodsError && !loading && (
              <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Apple className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Nu ai listă de alimente generată
                </h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  Generează lista de alimente permise și interzise adaptate profilului și obiectivelor tale
                </p>
                
                <button
                  onClick={generateFoods}
                  disabled={generating}
                  className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50 text-lg font-semibold"
                >
                  <Sparkles className="w-6 h-6" />
                  <span>{generating ? 'Generez...' : 'Generează Listă Alimente'}</span>
                </button>
                
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto">
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="text-2xl mb-2">✅</div>
                    <p className="text-sm font-medium text-gray-900">Alimente Permise</p>
                    <p className="text-xs text-gray-600">Pentru condiția ta</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4">
                    <div className="text-2xl mb-2">❌</div>
                    <p className="text-sm font-medium text-gray-900">Alimente Interzise</p>
                    <p className="text-xs text-gray-600">Ce să eviți</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="text-2xl mb-2">💡</div>
                    <p className="text-sm font-medium text-gray-900">Recomandări</p>
                    <p className="text-xs text-gray-600">Sfaturi personalizate</p>
                  </div>
                </div>
                
                {user?.lifestyleMode === 'HEALTHY' ? (
                  <p className="text-xs text-gray-500 mt-6">
                    ℹ️ Asigură-te că ai setat obiectivul de sănătate
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 mt-6">
                    ℹ️ Asigură-te că ai completat profilul cu obiectivul principal
                  </p>
                )}
              </div>
            )}

            {foodsError && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                  <div>
                    <p className="font-medium text-red-900">Eroare</p>
                    <p className="text-sm text-red-700">{foodsError}</p>
                    {user?.lifestyleMode === 'HEALTHY' ? (
                      <p className="text-xs text-red-600 mt-2">
                        Asigură-te că ai setat obiectivul de sănătate în profil.
                      </p>
                    ) : (
                      <p className="text-xs text-red-600 mt-2">
                        Asigură-te că ai completat profilul cu obiectivul principal.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {foods && (
              <>
                {/* Refresh Button */}
                <div className="bg-white rounded-2xl shadow-xl p-4">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                      <Sparkles className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Date generate cu AI</p>
                        <p className="text-xs text-gray-600">Bazate pe ghiduri de nutriție recunoscute</p>
                      </div>
                    </div>
                    <button
                      onClick={refreshFoods}
                      disabled={refreshing}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-all"
                      title="Regenerare disponibilă la 7 zile"
                    >
                      <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                      <span className="text-sm">{refreshing ? 'Regenerez...' : 'Regenerează'}</span>
                    </button>
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500">
                      ℹ️ Regenerarea este disponibilă o dată la 7 zile. Datele din profil nu se schimbă frecvent.
                    </p>
                  </div>
                </div>

                {/* Recommendations */}
                {foods.recommendations && (
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-2xl p-6">
                    <div className="flex items-start gap-3">
                      <Sparkles className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-bold text-green-900 mb-2">Recomandări AI</h3>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {foods.recommendations}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Allowed Foods */}
                <div className="bg-white rounded-2xl shadow-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">Alimente Permise</h2>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {foods.allowed.map((food, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200"
                      >
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                        <span className="text-sm text-gray-900 capitalize">{food}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Forbidden Foods */}
                <div className="bg-white rounded-2xl shadow-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                      <AlertCircle className="w-6 h-6 text-red-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">Alimente Interzise</h2>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {foods.forbidden.map((food, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 p-3 bg-red-50 rounded-lg border border-red-200"
                      >
                        <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                        <span className="text-sm text-gray-900 capitalize">{food}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Meal Plans Tab */}
        {tab === 'plans' && (
          <div className="space-y-6">
            {/* Food Preferences Button */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-2xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Settings2 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Preferințe Alimentare</p>
                  <p className="text-xs text-gray-500">Setează ce alimente vrei sau nu vrei în plan</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/nutrition/preferences')}
                className="px-4 py-2 bg-purple-500 text-white rounded-xl text-sm font-semibold hover:bg-purple-600 transition-all"
              >
                Setează
              </button>
            </div>

            {/* Generate Buttons */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Generează Plan Nou</h2>
              <div>
                <button
                  onClick={() => generateMealPlan(7)}
                  disabled={generating}
                  className="w-full flex items-center justify-center gap-3 p-6 bg-gradient-to-br from-green-500 to-blue-600 text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50"
                >
                  {generating ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    <Calendar className="w-6 h-6" />
                  )}
                  <div className="text-left">
                    <p className="font-bold text-lg">Plan 7 Zile</p>
                    <p className="text-xs opacity-90">O săptămână de mese personalizate</p>
                  </div>
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-4 text-center">
                ✨ Planuri personalizate generate automat bazate pe profilul tău și preferințele tale
              </p>
            </div>

            {/* Saved Plans List */}
            {mealPlans.length > 0 && (
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <ChefHat className="w-6 h-6 text-blue-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Planurile Mele</h2>
                </div>

                <div className="space-y-3">
                  {mealPlans.map((plan) => (
                    <div
                      key={plan.id}
                      className="p-4 border-2 border-gray-200 rounded-xl hover:border-green-400 transition-all group"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div
                          className="flex-1 cursor-pointer"
                          onClick={() => navigate('/nutrition/plan/' + plan.id, { state: { plan } })}
                        >
                          <p className="font-medium text-gray-900 group-hover:text-green-600 transition-colors">
                            Plan {plan.days} zile
                          </p>
                          <p className="text-sm text-gray-500">
                            {new Date(plan.createdAt).toLocaleDateString('ro-RO')}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={(e) => { e.stopPropagation(); setShoppingListPlan(plan); }}
                            className="flex items-center gap-1.5 px-3 py-2 bg-green-50 border border-green-200 text-green-700 rounded-lg text-xs font-semibold hover:bg-green-100 transition-all"
                            title="Generează lista de cumpărături"
                          >
                            <ShoppingCart className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">Listă</span>
                          </button>
                          <button
                            onClick={() => navigate('/nutrition/plan/' + plan.id, { state: { plan } })}
                            className="flex items-center gap-1.5 px-3 py-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-xs font-semibold hover:bg-blue-100 transition-all"
                            title="Vezi detalii plan"
                          >
                            <ChefHat className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">Detalii</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {mealPlans.length === 0 && !generating && (
              <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
                <ChefHat className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Nu ai planuri de mese
                </h3>
                <p className="text-gray-600">
                  Generează primul tău plan de mese personalizat cu AI
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Fitness Nutrition Disclaimer */}
      <div className="max-w-7xl mx-auto px-4 pb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-xs text-blue-800 leading-relaxed">
            <strong>ℹ️ Informație:</strong> Planurile nutriționale și listele de alimente sunt generate de <strong>FII FIT AI</strong> și personalizate în funcție de obiectivul tău fitness (datele tale sunt procesate de FII FIT AI pentru personalizare). Recomandările au caracter informativ și educațional. <strong>Consultați un nutriționist sportiv sau dietetician certificat</strong> înainte de a face modificări semnificative în alimentație. Surse: <a href="https://www.issn.net" target="_blank" rel="noopener noreferrer" className="underline">ISSN</a>, <a href="https://www.who.int/news-room/fact-sheets/detail/healthy-diet" target="_blank" rel="noopener noreferrer" className="underline">OMS</a>, <a href="https://www.efsa.europa.eu/en/topics/topic/dietary-reference-values" target="_blank" rel="noopener noreferrer" className="underline">EFSA</a>, <a href="https://www.acsm.org" target="_blank" rel="noopener noreferrer" className="underline">ACSM</a>.
          </p>
        </div>
      </div>

      {/* AI Consent Modal */}
      <AIConsentModal
        isOpen={showAIConsent}
        onAccept={() => setShowAIConsent(false)}
        onDecline={() => navigate('/dashboard')}
      />

      {/* Progress Modals */}
      <FoodProgressModal
        isOpen={showFoodProgress}
        onComplete={handleFoodProgressComplete}
        onError={handleFoodProgressError}
        onDismiss={() => {
          setShowFoodProgress(false);
          setGenerating(false);
        }}
      />
      
      <MealPlanProgressModal
        isOpen={showProgress}
        onComplete={handleProgressComplete}
        onError={handleProgressError}
        onDismiss={() => {
          setShowProgress(false);
          setGenerating(false);
        }}
      />

      {/* Insufficient Credits Modal */}
      <InsufficientCreditsModal
        isOpen={creditsModal.open}
        available={creditsModal.available}
        required={creditsModal.required}
        onClose={() => setCreditsModal({ open: false, available: 0, required: 0 })}
      />

      {/* Shopping List Modal */}
      {shoppingListPlan && (
        <ShoppingListModal
          isOpen={!!shoppingListPlan}
          onClose={() => setShoppingListPlan(null)}
          planData={shoppingListPlan.data}
          planDays={shoppingListPlan.days}
          planDate={shoppingListPlan.createdAt}
        />
      )}
    </div>
  );
}
